import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import OpcChatView from "../_shared/OpcChatView/OpcChatView";
import OpcHeader from "../_shared/OpcHeader/OpcHeader";
import {
  OpcPageError,
  OpcPageLoading,
} from "../_shared/OpcPageState/OpcPageState";
import okrPlansCoachStore, {
  useOkrPlansCoachStore,
} from "../_store/okrPlansCoachStore";
import useCoachPlan from "../_hooks/useCoachPlan";
import {
  companyPath,
  dashboardPath,
  planPath,
} from "../_config/okrPlansCoachRoutes";
import OwnerCard, { parseOwnerCard } from "./OwnerCard/OwnerCard";
import "../../_shared/ProjectStyles/ProjectsList.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One of the owner's chats with their AI coach — read-only (BE 2.7; C2,
// CD25, CD33): the SME-look `OpcChatView`, read-only (no input), on
// `GET …/chats/:chat/messages`. Cards show their final state.
// ═══════════════════════════════════════════════════════════════════════════

const selectLastCompany = (s) => s.lastCompany;

const OkrPlansCoachChat = () => {
  const { planUniqueId: planId, chatUniqueId: chatId } = useParams();
  const history = useHistory();
  const { plan } = useCoachPlan(planId);
  const lastCompany = useOkrPlansCoachStore(selectLastCompany);
  const selectChat = useCallback((s) => s.chats[chatId], [chatId]);
  const data = useOkrPlansCoachStore(selectChat);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    okrPlansCoachStore
      .getState()
      .fetchChat(planId, chatId)
      .catch((err) => setError(err));
  }, [planId, chatId]);
  useEffect(load, [load]);

  const toPlan = () => history.push(planPath(planId));
  const crumbs = [
    { title: "Dashboard", go: () => history.push(dashboardPath()) },
    ...(lastCompany
      ? [
          {
            title: lastCompany.name,
            go: () =>
              history.push(
                companyPath(lastCompany.id, lastCompany.quarterCompanyId),
              ),
          },
        ]
      : []),
    { title: plan?.objective?.text || "OKR plan", go: toPlan },
    { title: data?.chat?.name || "Chat" },
  ];

  let content;
  if (error)
    content = (
      <OpcPageError
        error={error}
        onRetry={load}
        onBack={toPlan}
        backLabel="Plan"
      />
    );
  else if (!data) content = <OpcPageLoading kind="chat" />;
  else {
    // Plain rows render as bubbles; card rows (JSON) render via
    // `renderCustomMessage`, so they get a sender the view doesn't know.
    const messages = (data.messages || []).map((m) => {
      const card = m.sender !== "user" ? parseOwnerCard(m) : null;
      return card
        ? { ...m, sender: "okr_plan_card", card, files: [] }
        : { files: [], ...m };
    });
    content = (
      <div className="py-3" id="project-chat-conversation">
        <OpcChatView
          messages={messages}
          readOnly
          renderCustomMessage={(m, i) =>
            m.card ? <OwnerCard key={m.message_id || i} card={m.card} /> : null
          }
        />
      </div>
    );
  }

  return (
    <div className="opc-screen">
      <OpcHeader crumbs={crumbs} />
      {content}
    </div>
  );
};

export default OkrPlansCoachChat;
